import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid, Legend } from "recharts";
import { BarChart3, PieChart as PieIcon, Palette } from "lucide-react";
import { pln } from "@/lib/api";

const COLORS = ["#F59E0B", "#2563EB", "#10B981", "#A855F7", "#F43F5E", "#14B8A6", "#F97316", "#64748B"];
const tip = { contentStyle: { background: "#161B22", border: "1px solid #30363D", borderRadius: 10, fontSize: 12, boxShadow: "0 8px 24px rgba(0,0,0,.4)" }, labelStyle: { color: "#F0F6FC", fontWeight: 700 }, cursor: { fill: "rgba(245,158,11,0.06)" } };

const Panel = ({ title, icon: Icon, children, testId }) => (
  <div data-testid={testId} className="rounded-2xl border border-border bg-card p-5 transition-colors duration-300 hover:border-amber-500/40">
    <div className="mb-4 flex items-center gap-2.5">
      <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-400/15 text-amber-400"><Icon className="h-4 w-4" /></span>
      <h3 className="font-display text-sm font-bold uppercase tracking-wider">{title}</h3>
    </div>
    <div className="h-64">{children}</div>
  </div>
);

const Empty = () => <div className="h-full grid place-items-center text-sm text-muted-foreground">Brak danych</div>;

export const Charts = ({ stats }) => {
  if (!stats) return null;
  const month = stats.by_month.map((m) => ({ ...m, label: m.month.slice(5) + "/" + m.month.slice(2, 4) }));
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Panel title="Sztuki i prowizja / miesiąc" icon={BarChart3} testId="chart-monthly">
        {month.length ? (
          <ResponsiveContainer>
            <BarChart data={month} barGap={4}>
              <defs>
                <linearGradient id="gQty" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" /><stop offset="100%" stopColor="#1D4ED8" /></linearGradient>
                <linearGradient id="gComm" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FBBF24" /><stop offset="100%" stopColor="#F59E0B" /></linearGradient>
              </defs>
              <CartesianGrid stroke="#30363D" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#8B949E" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#8B949E" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip {...tip} formatter={(v, n) => (n === "Prowizja" ? pln(v) : v)} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="qty" name="Sztuki" fill="url(#gQty)" radius={[5, 5, 0, 0]} maxBarSize={34} />
              <Bar dataKey="commission" name="Prowizja" fill="url(#gComm)" radius={[5, 5, 0, 0]} maxBarSize={34} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>
      <Panel title="Najpopularniejsze modele" icon={PieIcon} testId="chart-models">
        {stats.by_model.length ? (
          <ResponsiveContainer>
            <PieChart>
              <Pie data={stats.by_model} dataKey="value" nameKey="name" innerRadius={52} outerRadius={85} paddingAngle={3} stroke="#161B22" strokeWidth={2}>
                {stats.by_model.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip {...tip} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>
      <Panel title="Kolory (top 8)" icon={Palette} testId="chart-colors">
        {stats.by_color.length ? (
          <ResponsiveContainer>
            <BarChart data={stats.by_color} layout="vertical" margin={{ left: 10 }}>
              <XAxis type="number" stroke="#8B949E" fontSize={11} hide />
              <YAxis type="category" dataKey="name" stroke="#8B949E" fontSize={10} width={95} tickLine={false} axisLine={false} />
              <Tooltip {...tip} />
              <Bar dataKey="value" name="Sztuki" fill="#10B981" radius={[0, 5, 5, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        ) : <Empty />}
      </Panel>
    </div>
  );
};
